import React, { FC } from 'react';
import { graphql } from 'gatsby';
import classNames from 'classnames';

import { Layout, Container } from 'layout';
import BreadCrumbs from 'common/BreadCrumbs';
import ArticleBody from 'components/ArticleBody';
import ArticleReferences from 'components/ArticleReferences';
import TargetingSection from 'components/TargetingSection';
import FeaturedProducts from 'components/FeaturedProducts';
import FeaturedArticles from 'components/FeaturedArticles';
import HeroBanner from 'components/HeroBanner';
import SecondaryNavigationWithLinks from 'components/SecondaryNavigationWithLinks';
import DangerouslySetInnerHtml from 'common/DangerouslySetInnerHtml';

import { isNotEmptyArray } from 'utils/array';
import { schemaService } from 'utils/schemaService';

import { IPropsArticleSymptomsPage } from './models';

import './ArticleSymptomsPage.scss';

// activeColor is a part of the future ProductFamily functionality
const activeColor = '#FDBD21';

const ArticleSymptomsPage: FC<IPropsArticleSymptomsPage> = ({
  pageContext: { breadcrumbs, lang, link },
  data: {
    articleSymptoms: {
      seo,
      defaultCompositions,
      articleHeroBanner,
      articleTableOfContentsTitle,
      secondaryNavigationWithLinks,
      articleBody,
      articleReferenceSection,
      targetingSection,
      featuredArticlesList,
      featuredProductsList,
      selectedProductsIds,
      createDate,
      updateDate,
      introduction,
      isCampaignPage,
      disclaimer,
    },
    featuredArticlesByLink,
    featuredArticlesByTag,
    featuredArticlesSymptomsByLink,
    featuredArticlesSymptomsByTag,
    featuredArticlesAboutStrepsilsByLink,
    featuredArticlesAboutStrepsilsByTag,
    featuredProductsByLink,
    featuredProductsByTag,
    featuredProductsByLinkInBody,
    featuredProductsByTagInBody,
    articleSettings,
  },
}) => (
  <Layout
    defaultCompositions={defaultCompositions}
    seo={seo}
    anefpBlockCustomization={{ isHideBlock: true }}
    className={classNames('article-symptoms-page', {
      'campaign-page-bg': isCampaignPage,
    })}
    additionalStructuredSchemas={[
      schemaService.schemaTypes.NewsArticle,
      schemaService.schemaTypes.BreadcrumbList,
    ]}
    dynamicStructuredSchemaValues={{
      breadcrumbs,
      pageUrl: link,
      entityTitle: articleHeroBanner?.[0]?.properties?.title,
      entityImageUrl: articleHeroBanner?.[0]?.properties?.image?.fallbackUrl,
      entityDescription: seo?.seoMetaDescription,
      createDate,
      updateDate,
      introduction,
    }}
  >
    {!isCampaignPage ? (
      <Container>
        <BreadCrumbs breadcrumbs={breadcrumbs} />
      </Container>
    ) : null}
    {articleHeroBanner ? (
      <HeroBanner
        heroBanner={articleHeroBanner}
        showTitleOnTop={!isCampaignPage}
        hideOnMobile={!isCampaignPage}
      />
    ) : null}
    <Container>
      {secondaryNavigationWithLinks ? (
        <SecondaryNavigationWithLinks secondaryNavigationWithLinks={secondaryNavigationWithLinks} />
      ) : null}
      <ArticleBody
        articleBody={articleBody}
        defaultCompositions={defaultCompositions}
        lang={lang}
        featuredProductsByLinkInBody={featuredProductsByLinkInBody}
        featuredProductsByTagInBody={featuredProductsByTagInBody}
        articleTableOfContentsTitle={articleTableOfContentsTitle}
        selectedProductsIds={selectedProductsIds}
        articleSettings={articleSettings}
        createDate={createDate}
        updateDate={updateDate}
        isCampaignPage={isCampaignPage}
      />
    </Container>
    {isNotEmptyArray(featuredArticlesList) ? (
      <FeaturedArticles
        featuredArticlesList={featuredArticlesList}
        featuredArticlesByLink={[
          ...featuredArticlesAboutStrepsilsByLink.nodes,
          ...featuredArticlesSymptomsByLink.nodes,
          ...featuredArticlesByLink.nodes,
        ]}
        featuredArticlesByTag={[
          ...featuredArticlesAboutStrepsilsByTag.nodes,
          ...featuredArticlesSymptomsByTag.nodes,
          ...featuredArticlesByTag.nodes,
        ]}
        dir={defaultCompositions.siteSettings.dir[0]}
        ariaLabelPrev={defaultCompositions.siteSettings.ariaLabelPrev}
        ariaLabelNext={defaultCompositions.siteSettings.ariaLabelNext}
        isCampaign={isCampaignPage}
      />
    ) : null}
    {isNotEmptyArray(featuredProductsList) ? (
      <FeaturedProducts
        featuredProducts={featuredProductsList}
        featuredProductsByLink={featuredProductsByLink.nodes}
        featuredProductsByTag={featuredProductsByTag.nodes}
        productsOrder={featuredProductsList?.[0]?.properties?.selectedProductsIds || []}
        dir={defaultCompositions.siteSettings.dir[0]}
        ariaLabelPrev={defaultCompositions.siteSettings.ariaLabelPrev}
        ariaLabelNext={defaultCompositions.siteSettings.ariaLabelNext}
        activeColor={activeColor}
        isCampaign={isCampaignPage}
      />
    ) : null}
    {isNotEmptyArray(targetingSection) ? (
      <Container>
        <TargetingSection
          targetingSection={targetingSection}
          ariaLabelPrev={defaultCompositions.siteSettings.ariaLabelPrev}
          ariaLabelNext={defaultCompositions.siteSettings.ariaLabelNext}
          productQuizData={defaultCompositions.productQuiz}
          lang={lang}
        />
      </Container>
    ) : null}
    {articleReferenceSection[0] ? (
      <ArticleReferences articleReferenceSection={articleReferenceSection[0]} />
    ) : null}
    {disclaimer ? (
      <Container>
        <DangerouslySetInnerHtml html={disclaimer} className="disclaimer" />
      </Container>
    ) : null}
  </Layout>
);

export const query = graphql`
  query ArticleSymptomsPage(
    $link: String = ""
    $lang: String!
    $selectedArticlesLinks: [String]
    $selectedArticlesTag: Int
    $selectedProductsLinks: [String]
    $selectedProductTag: Int
    $featuredProductsURLsInBody: [String]
    $featuredProductsTagInBody: Int
  ) {
    articleSymptoms(link: { eq: $link }) {
      createDate
      updateDate
      seo {
        ...FragmentSeo
      }
      defaultCompositions {
        ...FragmentDefaultCompositions
      }
      articleHeroBanner {
        ...FragmentHeroBanner
      }
      articleTableOfContentsTitle
      secondaryNavigationWithLinks {
        ...FragmentSecondaryNavigationWithLinks
      }
      articleBody {
        ...FragmentArticleBody
      }
      articleReferenceSection {
        ...FragmentArticleReference
      }
      targetingSection {
        ...FragmentTargetingSection
      }
      featuredArticlesList {
        ...FragmentFeaturedArticles
      }
      featuredProductsList {
        ...FragmentFeaturedProducts
      }
      selectedProductsIds: featuredProductsURLsInBody {
        id
      }
      introduction
      isCampaignPage
      disclaimer
    }
    featuredArticlesSymptomsByLink: allArticleSymptoms(
      filter: { link: { in: $selectedArticlesLinks }, lang: { eq: $lang } }
    ) {
      nodes {
        ...FragmentArticleSymptomsCardRelated
      }
    }
    featuredArticlesSymptomsByTag: allArticleSymptoms(
      filter: { tags: { elemMatch: { id: { eq: $selectedArticlesTag } } }, lang: { eq: $lang } }
    ) {
      nodes {
        ...FragmentArticleSymptomsCardRelated
      }
    }
    featuredArticlesByLink: allArticle(
      filter: { link: { in: $selectedArticlesLinks }, lang: { eq: $lang } }
    ) {
      nodes {
        ...FragmentArticleCardRelated
      }
    }
    featuredArticlesByTag: allArticle(
      filter: { tags: { elemMatch: { id: { eq: $selectedArticlesTag } } }, lang: { eq: $lang } }
    ) {
      nodes {
        ...FragmentArticleCardRelated
      }
    }
    featuredArticlesAboutStrepsilsByLink: allArticleAboutStrepsils(
      filter: { link: { in: $selectedArticlesLinks }, lang: { eq: $lang } }
    ) {
      nodes {
        ...FragmentArticleAboutStrepsilsCardRelated
      }
    }
    featuredArticlesAboutStrepsilsByTag: allArticleAboutStrepsils(
      filter: { tags: { elemMatch: { id: { eq: $selectedArticlesTag } } }, lang: { eq: $lang } }
    ) {
      nodes {
        ...FragmentArticleAboutStrepsilsCardRelated
      }
    }
    featuredProductsByLink: allProduct(
      filter: { link: { in: $selectedProductsLinks }, lang: { eq: $lang } }
    ) {
      nodes {
        ...FragmentProductCardRelated
      }
    }
    featuredProductsByTag: allProduct(
      filter: { tags: { elemMatch: { id: { eq: $selectedProductTag } } }, lang: { eq: $lang } }
    ) {
      nodes {
        ...FragmentProductCardRelated
      }
    }
    featuredProductsByLinkInBody: allProduct(
      filter: { link: { in: $featuredProductsURLsInBody }, lang: { eq: $lang } }
    ) {
      nodes {
        ...FragmentProductCardRelated
      }
    }
    featuredProductsByTagInBody: allProduct(
      filter: {
        tags: { elemMatch: { id: { eq: $featuredProductsTagInBody } } }
        lang: { eq: $lang }
      }
    ) {
      nodes {
        ...FragmentProductCardRelated
      }
    }
    articleSettings(lang: { eq: $lang }) {
      ...FragmentArticleSettings
    }
  }
`;

export default ArticleSymptomsPage;
